import type { AssetClass } from '@vizit/api';
import { ButtonSize, Text, TextColor, TextSize, TextWeight, ToggleButton, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { useShowFavorites } from '@vizit/feature-flags';
import { ArrowNarrowDownIcon, ArrowNarrowUpIcon, ClockRewindIcon, HeartIcon, Image01Icon } from '@vizit/icons';
import { ClearableSelect, type ClearableSelectItem } from '@vizit/organisms';
import { cx } from '@vizit/theme';
import { CollectionAssetSortOption } from '../collection-asset-grid';

const SORT_ITEMS: ClearableSelectItem[] = [
  {
    id: CollectionAssetSortOption.DateNewest,
    label: 'Newest',
    icon: <ClockRewindIcon size={14} aria-hidden />,
  },
  {
    id: CollectionAssetSortOption.ScoreDesc,
    label: 'Score: high to low',
    icon: <ArrowNarrowDownIcon size={14} aria-hidden />,
  },
  {
    id: CollectionAssetSortOption.ScoreAsc,
    label: 'Score: low to high',
    icon: <ArrowNarrowUpIcon size={14} aria-hidden />,
  },
];

interface CollectionAssetToolbarProps {
  /**
   * How to sort assets within the collection
   */
  sortOption: CollectionAssetSortOption;
  /**
   * When true, generated (Spark) images are hidden
   */
  hideSparkImages: boolean;
  /**
   * When true, only favorited assets are shown
   */
  favoritesOnly: boolean;
  /**
   * The asset classes available to filter by — the classes actually present in the collection,
   * in canonical display order. When empty (and not loading), the asset-class filter is not shown.
   */
  classificationOptions: AssetClass[];
  /**
   * Whether the classification options are still loading. Keeps the filter mounted (showing its
   * placeholder) during the initial load so it doesn't pop in once the options resolve.
   * @default false
   */
  isClassificationsLoading?: boolean;
  /**
   * The currently selected asset class, or `null` when "All asset classes" is selected
   */
  selectedClassification: AssetClass | null;
  /**
   * Called when the user changes the sort option
   */
  onSortOptionChange: (option: CollectionAssetSortOption) => void;
  /**
   * Called when the user toggles the uploads-only filter; `hide` is true when only uploads
   * are shown (Spark images hidden)
   */
  onSparkVisibilityChange: (hide: boolean) => void;
  /**
   * Called when the user toggles the favorites-only filter
   */
  onFavoritesOnlyChange: (favoritesOnly: boolean) => void;
  /**
   * Called when the user changes the asset-class filter; `null` clears it (all classes)
   */
  onClassificationChange: (classification: AssetClass | null) => void;
  /**
   * Additional CSS classes to apply to the toolbar container
   */
  className?: string;
}

/**
 * Presentational toolbar for the collection-details page: an asset-class filter and an
 * uploads-only toggle (flush left) and a sort control (right). Purely controlled —
 * all state lives with the caller so selections can be persisted in the URL.
 *
 * @param props - The component props
 */
function CollectionAssetToolbar(props: CollectionAssetToolbarProps) {
  const {
    sortOption,
    hideSparkImages,
    favoritesOnly,
    classificationOptions,
    isClassificationsLoading = false,
    selectedClassification,
    onSortOptionChange,
    onSparkVisibilityChange,
    onFavoritesOnlyChange,
    onClassificationChange,
    className,
  } = props;

  const showFavorites = useShowFavorites();

  const classificationItems: ClearableSelectItem[] = classificationOptions.map((classification) => ({
    id: classification,
    label: classification,
  }));

  // Show the filter once the collection is known to have classes, and keep it up during the initial
  // load so it doesn't blink in. It only stays hidden for a collection that genuinely has none.
  const showClassificationFilter = isClassificationsLoading || classificationItems.length > 0;

  return (
    <div className={cx('flex w-full items-center justify-between', className)}>
      <div className="flex items-center gap-4">
        {showClassificationFilter && (
          <ClearableSelect
            ariaLabel="Filter by asset class"
            placeholder="All asset classes"
            items={classificationItems}
            selectedKey={selectedClassification}
            onSelectionChange={(key) => onClassificationChange(key as AssetClass | null)}
            isClearable={false}
            className="min-w-[256px]"
            data-testid="collection-asset-class-filter"
          />
        )}
        <TooltipTrigger delay={300}>
          <ToggleButton
            aria-label="Show uploads only"
            isSelected={hideSparkImages}
            onChange={onSparkVisibilityChange}
            size={ButtonSize.Md}
            iconStart={<Image01Icon size={20} aria-hidden />}
            data-testid="collection-asset-spark-toggle"
          />
          <Tooltip>Show uploads only</Tooltip>
        </TooltipTrigger>
        {showFavorites && (
          <TooltipTrigger delay={300}>
            <ToggleButton
              aria-label="Show favorites only"
              isSelected={favoritesOnly}
              onChange={onFavoritesOnlyChange}
              size={ButtonSize.Md}
              iconStart={<HeartIcon size={20} aria-hidden />}
              data-testid="collection-asset-favorites-toggle"
            />
            <Tooltip>Show favorites only</Tooltip>
          </TooltipTrigger>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-4">
        <Text size={TextSize.Sm} weight={TextWeight.Semibold} color={TextColor.Tertiary}>
          Sort by:
        </Text>
        <ClearableSelect
          ariaLabel="Collection asset sort"
          placeholder="Newest"
          items={SORT_ITEMS}
          selectedKey={sortOption}
          onSelectionChange={(key) => key && onSortOptionChange(key as CollectionAssetSortOption)}
          includeAllOption={false}
          isClearable={false}
          className="min-w-[200px]"
          data-testid="collection-asset-sort-filter"
        />
      </div>
    </div>
  );
}

export { CollectionAssetToolbar };
export type { CollectionAssetToolbarProps };
